// src/screens/ProfileSetup.jsx
// Einmalige Abfrage nach der Erstanmeldung: Vorname, Geschlecht, Geburtsdatum.
// Gespeichert in Supabase (`user_details`), damit die Beta-Tester sichtbar sind.
import React, { useState } from "react";
import { saveUserDetails } from "../lib/remoteProfiles";
import { Button, Note, ScreenHeader, Select, Stack, TextInput } from "../components/ui";

// Gespeicherte Werte (Spalte `gender`) → angezeigte Beschriftung
const GENDER_OPTIONS = [
  { value: "weiblich", label: "Weiblich" },
  { value: "maennlich", label: "Männlich" },
  { value: "divers", label: "Divers" },
  { value: "keine_angabe", label: "Keine Angabe" },
];

const MIN_AGE = 5;
const MAX_AGE = 120;

// "YYYY-MM-DD" in lokaler Zeit (toISOString wäre UTC und kann einen Tag abweichen)
function toDateString(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function yearsAgo(years) {
  const d = new Date();
  d.setFullYear(d.getFullYear() - years);
  return toDateString(d);
}

export default function ProfileSetup({ user, onDone }) {
  const [firstName, setFirstName] = useState("");
  const [gender, setGender] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Datumsfeld liefert "YYYY-MM-DD" → Strings lassen sich direkt vergleichen
  const minDate = yearsAgo(MAX_AGE);
  const maxDate = yearsAgo(MIN_AGE);
  const birthDateValid = Boolean(birthDate) && birthDate >= minDate && birthDate <= maxDate;

  const canSave = firstName.trim().length > 0 && Boolean(gender) && birthDateValid && !saving;

  async function handleSave() {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      await saveUserDetails(user.id, {
        firstName: firstName.trim(),
        gender,
        birthDate,
        email: user.email,
      });
      onDone();
    } catch (e) {
      console.warn("saveUserDetails failed:", e);
      setError("Speichern hat nicht geklappt. Bitte versuch es noch einmal.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <ScreenHeader
        title="Willkommen bei fluent!"
        subtitle="Erzähl uns kurz etwas über dich."
      />

      <Stack gap={4}>
        <div>
          <label className="ui-label" htmlFor="profile-first-name">
            Vorname
          </label>
          <TextInput
            id="profile-first-name"
            type="text"
            autoComplete="given-name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            maxLength={50}
          />
        </div>

        <div>
          <label className="ui-label" htmlFor="profile-gender">
            Geschlecht
          </label>
          <Select
            id="profile-gender"
            options={GENDER_OPTIONS}
            placeholder="Bitte auswählen"
            value={gender}
            onChange={(e) => setGender(e.target.value)}
          />
        </div>

        <div>
          <label className="ui-label" htmlFor="profile-birth-date">
            Geburtsdatum
          </label>
          <TextInput
            id="profile-birth-date"
            type="date"
            autoComplete="bday"
            min={minDate}
            max={maxDate}
            value={birthDate}
            onChange={(e) => setBirthDate(e.target.value)}
          />
          {birthDate && !birthDateValid && (
            <Note tone="error">Bitte prüfe dein Geburtsdatum.</Note>
          )}
        </div>
      </Stack>

      <Stack gap={2} style={{ marginTop: "var(--fluent-space-5)" }}>
        <Button block onClick={handleSave} disabled={!canSave}>
          {saving ? "Speichern …" : "Weiter"}
        </Button>
        {error && <Note tone="error">{error}</Note>}
        <Note>Nur für die Auswertung der Beta, wird nicht weitergegeben.</Note>
      </Stack>
    </>
  );
}
